import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';
import { defaultProduct, DefaultProductType } from 'pages/Products/constants/defaultProduct';

import {
    BarcodeType,
    ProductState,
    ProductUpdateInput,
    useCreateProductMutation,
    useGetActiveCategoriesQuery,
    useGetActiveOrganizationsQuery,
    useGetProductArrangementsQuery,
    useGetProductPriceRangesQuery,
    useGetSuppliersQuery,
    useGetTagsQuery,
    useUpdateProductArrangementMutation,
} from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';

import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { formatFormArray, formatMultilineToArray } from 'pages/Products/utils';
import { DimensionsSection } from './ProductDetails/DimensionsSection';
import { GeneralInfoSection } from './ProductDetails/GeneralInfoSection';
import { NutritionalInfoSection } from './ProductDetails/NutritionalInfoSection';
import { SalesInfoSection } from './ProductDetails/SalesInfoSection';
import { StockAndSupplierInfoSection } from './ProductDetails/StockAndSupplierInfoSection';
import { TagsAndBadgesSection } from './ProductDetails/TagsAndBadgesSection';
import {
    ArrangementSelection,
    ProductArrangementCreationSection,
} from './ProductDetails/ProductArrangementCreationSection';

type Merge<A, B> = Omit<A, keyof B> & B;

type FormData = Merge<
    ProductUpdateInput,
    {
        access: { value: string; label: string }[];
        barcodes: { barcode: string; type: BarcodeType }[];
        diets: { value: string; label: string }[];
        preferences: { value: string; label: string }[];
        supplierShortageInfo: {
            inShortage: boolean;
            returnDate: string | null;
        };
        tagOptions: { value: string; label: string }[]; //object type used with UI Select, stocking as strings in DB
        searchKeywords: string | null;
    }
>;

export const ProductCreate = () => {
    const navigate = useNavigate();
    const [createProduct, { loading: creationLoading }] = useCreateProductMutation();
    const [updateProductArrangement] = useUpdateProductArrangementMutation();
    const [selectedArrangements, setSelectedArrangements] = useState<ArrangementSelection[]>([]);
    const initialProduct: DefaultProductType = { ...defaultProduct, nayaxProductId: null, odooProduct: null };

    const {
        loading: activeCategoriesLoading,
        data: activeCategoriesData,
        error: activeCategoriesError,
    } = useGetActiveCategoriesQuery();

    const {
        loading: productRangesLoading,
        data: productRangesData,
        error: productRangesError,
    } = useGetProductPriceRangesQuery();

    const {
        loading: organizationsLoading,
        data: organizationsData,
        error: organizationsError,
    } = useGetActiveOrganizationsQuery();

    const { loading: tagsLoading, data: tagsData, error: tagsError } = useGetTagsQuery();

    const { loading: suppliersLoading, data: suppliersData, error: suppliersError } = useGetSuppliersQuery();

    const {
        loading: productArrangementsLoading,
        data: productArrangementsData,
        error: productArrangementsError,
    } = useGetProductArrangementsQuery();

    const methods = useForm<FormData>({
        defaultValues: {
            categoryId: undefined,
            subcategoryId: null,
        },
    });
    const {
        handleSubmit,
        formState: { isDirty },
    } = methods;

    if (
        productRangesLoading ||
        activeCategoriesLoading ||
        tagsLoading ||
        organizationsLoading ||
        suppliersLoading ||
        productArrangementsLoading
    ) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (
        organizationsError ||
        !organizationsData ||
        productRangesError ||
        !productRangesData ||
        activeCategoriesError ||
        !activeCategoriesData ||
        tagsError ||
        !tagsData ||
        suppliersError ||
        !suppliersData ||
        productArrangementsError ||
        !productArrangementsData
    ) {
        throw new Error('Une erreur est survenue lors de la récupération des paramètres');
    }

    const { activeCategories: categories } = activeCategoriesData;
    const { activeOrganizations: organizations } = organizationsData;
    const { tags } = tagsData;

    const { productArrangementsQuery: productArrangements } = productArrangementsData;

    const addProductToArrangements = async (productId: string) => {
        const updatePromises = selectedArrangements.map((selection) => {
            const arrangement = productArrangements.find((pa) => pa._id === selection.arrangementId);
            if (!arrangement) return Promise.resolve();

            const newProductIdsWithStockMax = [
                ...arrangement.productIdsWithStockMax.map(({ productId, stockMax }) => ({
                    productId,
                    stockMax,
                })),
                {
                    productId,
                    stockMax: selection.stockMax,
                },
            ];

            return updateProductArrangement({
                variables: {
                    productArrangement: {
                        _id: arrangement._id,
                        name: arrangement.name,
                        reassortmentType: arrangement.reassortmentType,
                        productIdsWithStockMax: newProductIdsWithStockMax,
                    },
                },
            });
        });

        await Promise.all(updatePromises);
    };

    const onSubmit = handleSubmit(
        async (fields) => {
            const tagIds = formatFormArray(fields.tagOptions);
            const formattedAccess = formatFormArray(fields.access);
            const formattedBarcodes = fields.barcodes.map(({ barcode, type }) => ({ barcode, type }));
            const formattedDiets = formatFormArray(fields.diets);
            const formattedPreferences = formatFormArray(fields.preferences);
            const formattedSearchKeywords = formatMultilineToArray(fields.searchKeywords || '');

            const { tagOptions, ...fieldsWithoutTags } = fields;

            if (!fields.categoryId) {
                toast.error('Un produit doit être associé à une catégorie.');
                return;
            }

            const { data } = await createProduct({
                variables: {
                    fields: {
                        ...fieldsWithoutTags,
                        access: formattedAccess,
                        barcodes: formattedBarcodes,
                        diets: formattedDiets,
                        preferences: formattedPreferences,
                        searchKeywords: formattedSearchKeywords,
                        tagIds,
                        // This fields are not in form so we have to set them manually
                        isToBeArchived: false,
                        isBeingTested: false,
                        isPaused: false,
                        state: ProductState.Created,
                    },
                },
            });

            if (data) {
                const {
                    createProductResolver: { createdProductId, success, formErrors },
                } = data;
                if (success && createdProductId) {
                    try {
                        await addProductToArrangements(createdProductId);
                        toast.success(`Le produit "${fields.name}" a bien été créé !`);
                    } catch {
                        toast.warning(
                            `Le produit "${fields.name}" a été créé mais l'ajout aux gammes a échoué. Veuillez les ajouter manuellement.`,
                        );
                    }
                    navigate(`/product/${createdProductId}`);
                } else {
                    if (formErrors) {
                        formErrors.forEach(({ sectionName, sectionErrors }, index) => {
                            toast.error(
                                <span key={index}>
                                    Erreur dans la section "{sectionName}" :
                                    {sectionErrors.map(({ fieldName, fieldError }, index) => (
                                        <span key={index}>
                                            <br />- {fieldName} : "{fieldError}"
                                        </span>
                                    ))}
                                </span>,
                                { autoClose: false },
                            );
                        });

                        if (formErrors.length > 1) {
                            toast.info('Cliquez pour fermer toutes les notifications', {
                                autoClose: false,
                                onClick: () => toast.dismiss(),
                            });
                        }
                    } else {
                        throw Error("Une erreur inconnue s'est produite");
                    }
                }
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        },
        (_errors) => {
            toast.error("Le formulaire n'est pas valide. Veuillez corriger les erreurs avant de continuer.");
        },
    );

    const { productPriceRanges } = productRangesData;
    const { suppliers } = suppliersData;

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/products' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.productCreate} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={creationLoading}
                            saveTitle="Créer"
                            saveDataTest="create-button"
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection product={initialProduct} categories={categories} />
                            <SalesInfoSection
                                organizations={organizations}
                                product={initialProduct}
                                productPriceRanges={productPriceRanges}
                                isIncomingFromCreation
                            />
                            <ProductArrangementCreationSection
                                selectedArrangements={selectedArrangements}
                                onArrangementsChange={setSelectedArrangements}
                            />
                            <TagsAndBadgesSection tags={tags} isIncomingFromCreation />
                            <NutritionalInfoSection product={initialProduct} isIncomingFromCreation />
                            <DimensionsSection product={initialProduct} isIncomingFromCreation />
                            <StockAndSupplierInfoSection
                                product={initialProduct}
                                isIncomingFromCreation
                                suppliers={suppliers}
                            />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
